import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from './NavBar';

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Quanturama",
  "description": "Quanturama builds full digital presence, automation pipelines, and web3 integration for businesses operating globally.",
  "url": "https://quanturama.com/about",
  "mainEntity": {
    "@type": "Organization",
    "name": "Quanturama",
    "url": "https://quanturama.com",
    "foundingDate": "2016",
    "description": "Remote-first technology operator building automation systems, web3 infrastructure, and digital presence for clients across Europe and the United States.",
    "email": "hello@quanturama.com",
    "knowsAbout": [
      "Automation Pipelines",
      "Trading Bots",
      "Web3 Integration",
      "Decentralized Applications",
      "Digital Presence",
      "AI Agents"
    ]
  }
};

const PILLARS = [
  {
    label: 'Digital Presence',
    text: 'Web systems built to rank, convert, and represent your business accurately — from architecture to copy to schema.'
  },
  {
    label: 'Automation Pipelines',
    text: 'Trading bots, AI agents, workflow engines, and custom pipelines. Systems that operate without you in the loop.'
  },
  {
    label: 'Web3 Integration',
    text: 'DApps, smart contracts, token economies, and decentralized infrastructure on Polygon, BSC, TON, and beyond.'
  }
];

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>About | Quanturama</title>
        <meta name="description" content="Quanturama builds full digital presence, automation pipelines, and web3 integration. Remote-first, operating globally across Europe and the United States." />
        <link rel="canonical" href="https://quanturama.com/about" />
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>

      <NavBar />

      <main className="contact-page" style={{ paddingBottom: '6rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 1rem' }}>

          <p style={{ fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', opacity: 0.4, marginBottom: '2rem' }}>
            about
          </p>

          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: '1.5rem' }}>
            We build systems.
          </h1>

          <p style={{ fontSize: '1.05rem', lineHeight: 1.85, opacity: 0.75, marginBottom: '3rem', maxWidth: '580px' }}>
            Quanturama is a remote-first technology operator with nine years of active experience in crypto markets, web
            infrastructure, and decentralized systems. We work with a small number of clients at a time — businesses that
            need reliable, scalable infrastructure built to last.
          </p>

          <div style={{ display: 'grid', gap: '2rem', marginBottom: '4rem' }}>
            {PILLARS.map(({ label, text }) => (
              <div key={label} style={{ borderLeft: '1px solid rgba(255,255,255,0.08)', paddingLeft: '1.5rem' }}>
                <p style={{ fontSize: '11px', letterSpacing: '0.25em', textTransform: 'uppercase', opacity: 0.45, marginBottom: '0.5rem' }}>
                  {label}
                </p>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.75, opacity: 0.7 }}>{text}</p>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1.5rem', marginBottom: '4rem' }}>
            {[
              { n: '9+', label: 'Years active' },
              { n: '5', label: 'Live systems' },
              { n: '5', label: 'Chains deployed' },
              { n: 'EU / US', label: 'Primary markets' }
            ].map(({ n, label }) => (
              <div key={label} style={{ paddingTop: '1rem', borderTop: '0.5px solid rgba(255,255,255,0.07)' }}>
                <p style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.3rem' }}>{n}</p>
                <p style={{ fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.4 }}>{label}</p>
              </div>
            ))}
          </div>

          <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.07)', paddingTop: '2.5rem' }}>
            <p style={{ fontSize: '0.95rem', lineHeight: 1.8, opacity: 0.65, marginBottom: '1.5rem' }}>
              We operate remotely across Europe and the United States. Projects are taken on selectively — the fit matters
              as much as the brief.
            </p>
            <Link
              to="/contact"
              style={{
                display: 'inline-block',
                fontSize: '13px',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                borderBottom: '1px solid rgba(255,255,255,0.25)',
                paddingBottom: '2px',
                color: 'inherit',
                textDecoration: 'none',
                opacity: 0.8
              }}
            >
              Start a conversation →
            </Link>
          </div>

        </div>
      </main>
    </>
  );
};

export default About;
